import axios from 'axios';
import { appointmentServiceApi } from "../../../config/config";
// import CardViewMock from "../../../mock/card-view.json";
import { setupInterceptorsTo } from "../interceptors/axios.interceptos";
const axiosInstance = setupInterceptorsTo(axios.create());

export interface UpateUserProfile {
    firstname?: string,
    lastname?: string | null,
    gender?: "M" | "F" | "O" | null,
    weight?: number | null,
    height?: number | null,
    dob?: Date | null,
    image_path?: string,
    bp?: string | null,
    hr_spo2?: string | null
}

class AppointmentService {

    async getAppointments({ practiceSessionId, apptStatus, scheduleOn }: any): Promise<any> {
        try {
            const { data } = await axiosInstance.get(`${appointmentServiceApi.baseUrl}/api/v1/session/${practiceSessionId}/appointments`, {
                params: {
                    date: scheduleOn,
                    apptStatus: apptStatus
                }
            });
            return data;
        } catch (error) {
            console.log('Error in Get Appointment')
            return {
                data: []
            }
        }
    }

   async getSingleAppointmentDetail(appointmentId: number | string): Promise<any> {
        try {
            const { data } = await axiosInstance.get(`${appointmentServiceApi.baseUrl}/api/v1/session/appointments/${appointmentId}/print`);
            return data.data;
        } catch (error) {
            console.log('Error in Get Appointment')
            return {
                data: []
            }
        }
    }

    /**
     * The payment behind one appointment.
     *
     * Keyed by appointment rather than read out of the day's online payment
     * report: that report only covers confirmed appointments in the first
     * online session of the date, so a patient who paid and then cancelled
     * showed an empty payment view.
     */
    async getPaymentDetail(appointmentId: number | string): Promise<any> {
        try {
            const { data } = await axiosInstance.get(`${appointmentServiceApi.baseUrl}/api/v1/appointment/${appointmentId}/payment`);
            return data?.data ?? null;
        } catch (error) {
            console.log('Error in Get Payment Detail', error)
            return null;
        }
    }

    async makeAppointment(params: any): Promise<any> {
        try {
            return await axiosInstance.post(`${appointmentServiceApi.baseUrl}/api/v1/appointment/make`, params);
        } catch (error) {
            console.log('Error in Get Appointment')
            throw error;
        }
    }

    async cancelAppointment(params: any): Promise<any> {
        try {
            return await axiosInstance.put(`${appointmentServiceApi.baseUrl}/api/v1/appointment/cancel`, params);
        } catch (error) {
            console.error('Error in Cancel Appointment')
            throw error;
        }
    }

    async getPatient({ phnNumber }: any): Promise<any> {
        try {
            const { data } = await axiosInstance.get(`${appointmentServiceApi.baseUrl}/api/v1/patient/search?phoneNumber=${phnNumber}`);
            return data;
        } catch (error) {
            console.log('Error in getPatient', error)
            throw error;
        }
    }

    async reRescheduleAppt(params: any): Promise<any> {
        try {
            return await axiosInstance.put(`${appointmentServiceApi.baseUrl}/api/v1/appointment/reschedule`, params);
        } catch (error) {
            console.error('Error in reRescheduleAppt')
            throw error;
        }
    }

    /**
     * Get the card view details
     * @param practiceSessionId
     * @returns 
     */
    async getCardViewDetails(practiceSessionId: number) {
        try {
            const { data } = await axiosInstance.get(`${appointmentServiceApi.baseUrl}/api/v1/card-view?practiceSessionId=${practiceSessionId}`);
            return data?.data;
            // return CardViewMock;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Change card view status or move the apoointment status
     * @param params 
     */
    async cardViewChangeStatus(params: any) {
        try {
            const { data } = await axiosInstance.post(`${appointmentServiceApi.baseUrl}/api/v1/card-view`, params);
            return data;
        } catch (error) {
            throw error;
        }
    }


    /**
     * Update the user profile details
     * @param params 
     * @returns 
     */
    async upateUserProfile(id: number, params: UpateUserProfile) {
        try {
            const { data } = await axiosInstance.put(`${appointmentServiceApi.baseUrl}/api/v1/user/profile/${id}`, params);
            return data;
        } catch (error) {
            throw error;
        }
    }

    async savePatientProfile(
        { userProfileId, partyId }: { userProfileId?: number | string | null; partyId?: number | string | null },
        params: UpateUserProfile
    ) {
        if (Number.isSafeInteger(Number(userProfileId)) && Number(userProfileId) > 0) {
            return this.upateUserProfile(Number(userProfileId), params);
        }

        // Phone bookings can have a patient party before any profile is entered.
        if (!Number.isSafeInteger(Number(partyId)) || Number(partyId) <= 0) {
            throw new Error('No patient is linked to this appointment. Please select a patient before saving.');
        }

        const { data } = await axiosInstance.put(
            `${appointmentServiceApi.baseUrl}/api/v1/user/profile/party/${Number(partyId)}`,
            params
        );
        return data;
    }


    async  notifyPatientsWithAppointments(appointmentIds: number[], message: string): Promise<any> {
        try {
       
            const notificationResponse = await axiosInstance.post(`${appointmentServiceApi.baseUrl}/api/v1/appointment/notify-patient`, {
                appointmentIds: appointmentIds,
                message: message
            });
    
            return notificationResponse.data; 
        } catch (error) {
            console.log('Error in Notify Patients with Appointments', error);
            throw error; 
        }
    }

    async initiateTwoLegCall(callFrom: string, callTo: string,callSid:string): Promise<any> {
    try {
        const { data } = await axiosInstance.get(`${appointmentServiceApi.baseUrl}/api/v1/ivr/two-leg-call`,  {params: {
        CallFrom: callFrom,
        CallTo: callTo,
        CallSid: ''
      }});

        return data;
    } catch (error) {
        console.error('Error in initiating two-leg call:', error);
        return { success: false, message: 'Call initiation failed' };
    }
}


async getNotificationStatus(apptId: string): Promise<any> {
    try {
        const { data } = await axiosInstance.get(
            `${appointmentServiceApi.baseUrl}/api/v1/ivr/notification-status/${apptId}`
        );
        return data;
    } catch (error) {
        console.log('Error in Get Notification Status', error);
        return {};
    }
}

async getPaymentFormData(id: string, body?: { patientData?: any }): Promise<any> {
  try {
    const { data } = await axiosInstance.post(
      `${appointmentServiceApi.baseUrl}/api/v1/ivr/appointment/${id}/payment-form-data`,
      body|| {}
    );
    return data;
  } catch (error) {
    console.log('Error in Get Payment Form Data');
    return {};
  }
} 
async getCashfreeStatus(orderId: string): Promise<any> {
  try {
    const { data } = await axiosInstance.get(
      `${appointmentServiceApi.baseUrl}/api/v1/ivr/cashfree/status-check/${orderId}`
    );
    return data;
  } catch (error) {
    console.log('Error in Get Cashfree Status', error);
    return {};
  }
}


async getPaymentFormDataOnline(id: string, body?: { patientData?: any }): Promise<any> {
  try {
    const { data } = await axiosInstance.post(
      `${appointmentServiceApi.baseUrl}/api/v1/ivr/appointment/${id}/payment-form-dataOnline`,
      body|| {}
    );
    return data;
  } catch (error) {
    console.log('Error in Get Payment Form Data');
    return {};
  }
}

async getCashfreeStatusOnline(orderId: string): Promise<any> {
  try {
    const { data } = await axiosInstance.get(
      `${appointmentServiceApi.baseUrl}/api/v1/ivr/cashfree/status-check-online/${orderId}`
    );
    return data;
  } catch (error) {
    console.log('Error in Get Cashfree Status', error);
    return {};
  }
}
async updateWalkinAppointment(params: any): Promise<any> {
    try {
        return await axiosInstance.post(`${appointmentServiceApi.baseUrl}/api/v1/appointment/walkin/update`, params);
    } catch (error) {
        console.log('Error in Update Walkin Appointment', error);
        throw error;
    }
}

}
const appointmentService = new AppointmentService();
export default appointmentService;
